import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Form, Button, Container, Alert } from 'react-bootstrap';
import Seo from '../Seo';
import { useSelector } from 'react-redux';
import { State } from '../../state';
import styles from './ContactForm.module.css';
import contactService from '../../services/contactService';
import { trackContactSubmit, trackEvent } from '../../util/analytics';

interface ContactFormData {
  companyName: string;
  fullName: string;
  email: string;
  phone: string;
  message: string;
}

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    companyName: '',
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });

  // Honeypot: a decoy field hidden from real users but visible to naive bots.
  // Real submissions leave it empty; a filled value marks the request as spam.
  const [honeypot, setHoneypot] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const { data: languageData } = useSelector((state: State) => state.languageReducer);

  const location = useLocation();
  const isEnglish =
    location.pathname === '/en' || location.pathname.startsWith('/en/');
  const kvkkHref = isEnglish ? '/en/kvkk' : '/kvkk';

  // Fire form_start once per mount, on first interaction: separates "the page
  // doesn't persuade" (no starts) from "the form loses them" (starts, no submit).
  const startedRef = useRef(false);
  const handleFormFocus = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent('form_start', { form: 'contact' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Silently drop bot submissions: the honeypot is only reachable by scripts.
    // Show the success state so a bot can't tell it was filtered, but skip the
    // network call, the analytics conversion, and the form reset.
    if (honeypot) {
      setSubmitStatus({
        type: 'success',
        message: languageData?.contact?.successMessage
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      await contactService.submitContactForm(formData);
      trackContactSubmit();
      setSubmitStatus({
        type: 'success',
        message: languageData?.contact?.successMessage
      });
      setFormData({
        companyName: '',
        fullName: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      trackEvent('form_error', { form: 'contact' });
      setSubmitStatus({
        type: 'error',
        message: languageData?.contact?.errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className={styles.contactFormContainer}>
      <Seo path="/contact" />
      <h1 className={styles.formTitle}>{languageData?.contact?.title}</h1>
      <p className={styles.formSubtitle}>{languageData?.contact?.subtitle}</p>

      {submitStatus.type && (
        <Alert variant={submitStatus.type === 'success' ? 'success' : 'danger'}>
          {submitStatus.message}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} onFocus={handleFormFocus} className={styles.form}>
        {/* Honeypot: off-screen and hidden from assistive tech / tab order.
            Humans never see or reach it; bots that fill every field trip it. */}
        <div className={styles.honeypot} aria-hidden="true">
          <label htmlFor="contact-website">Website</label>
          <input
            type="text"
            id="contact-website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <Form.Group className="mb-3">
          <Form.Label>{languageData?.contact?.companyName}</Form.Label>
          <Form.Control
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{languageData?.contact?.fullName}</Form.Label>
          <Form.Control
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{languageData?.contact?.email}</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{languageData?.contact?.phone}</Form.Label>
          <Form.Control
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{languageData?.contact?.message}</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            name="message"
            value={formData.message}
            onChange={handleChange}
          />
        </Form.Group>

        <Button 
          type="submit" 
          variant="primary" 
          disabled={isSubmitting}
          className={styles.submitButton}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {languageData?.contact?.submitting}
            </>
          ) : (
            languageData?.contact?.submit
          )}
        </Button>

        <p className={styles.formNote}>{languageData?.contact?.responseNote}</p>
        <p className={styles.kvkkNote}>
          {languageData?.contact?.kvkkNotice}{' '}
          <Link to={kvkkHref}>{languageData?.contact?.kvkkLinkText}</Link>.
        </p>
      </Form>
    </Container>
  );
};

export default ContactForm; 